import React from 'react';
import { Form, Select, Input, Button, DatePicker } from 'antd';
import { FormInstance } from 'antd/es/form';
import FormSection from '../FormSection';
import CurvDown from '../svg/CurvDown';

const { Option } = Select;

interface SexualHarassmentProps {
  form: FormInstance;
  isViewMode?: boolean;
}

const SexualHarassment: React.FC<SexualHarassmentProps> = ({ form, isViewMode }) => {
  const isView = isViewMode || new URLSearchParams(window.location.search).get('view') === 'true';
  const yesNoOptions = ['Yes', 'No'];
  const harassedByOptions = ['Supervisor', 'Coworker'];
  const whereConductOptions = [
    'Private office',
    'Work area',
    'Meeting room',
    'Public area',
    'Multiple locations'
  ];
  const bodyPartOptions = [
    'Arm', 'Hand', 'Shoulder', 'Back', 'Leg', 'Face', 'Hair', 'Other'
  ];
  const whatDoneOptions = [
    'Verbal warning',
    'Written warning',
    'Suspension',
    'Termination',
    'Transfer',
    'Training',
    'No action',
    'Other'
  ];
  const harassmentActions = [
    'Repeated suggestive comments about specific parts of your or their own body',
    'Repeated suggestive comments about your or their own clothes',
    'Repeatedly touched you over your objections',
    'Repeatedly asked you for dates over your objections',
    'Repeated suggestive words about you to others',
    'Repeated statements that you could advance in your job if you cooperated in an intimate relationship',
    'Repeated obscene gestures',
    'Repeated comments regarding his or her sex life',
    'Repeated disrobing in front of you',
    'Repeated statements that keeping your job depended on your cooperation in an intimate relationship',
    'Repeated statements that other terms or benefits of your job depended on your cooperation in an intimate relationship'
  ];
  const lossOptions = [
    'Had to take more sick time, personal time, comp time, vacation time, or time without pay',
    'Were unable to work overtime',
    'Lost advancement to a higher position',
    'Lost raises or bonuses',
    'Had no losses'
  ];

  const harassedByWatch = Form.useWatch(['sexual_harassment_info', 'harassed_by'], form);
  const disableIfHarassedBySupervisor = harassedByWatch === 'Supervisor';

  const notifiedSupervisorWatch = Form.useWatch(['sexual_harassment_info', 'notified_supervisor'], form);
  const actionTakenWatch = Form.useWatch(['sexual_harassment_info', 'action_taken'], form);

  const disableNotifiedFields = notifiedSupervisorWatch !== 'Yes';
  const disableActionTakenFields = actionTakenWatch !== 'Yes';

  return (
    <div className="flex flex-col w-full h-full sexual-harassment-container">
      <style dangerouslySetInnerHTML={{
        __html: `
        .sexual-harassment-container .ant-form-item-label {
          min-height: 58px !important;
          margin-bottom: 8px !important;
          display: flex !important;
          align-items: flex-end !important;
        }
        @media (max-width: 767px) {
          .sexual-harassment-container .ant-form-item-label {
            min-height: unset !important;
            margin-bottom: 0px !important;
            display: block !important;
          }
        }
        .sexual-harassment-container .ant-select-selector,
        .sexual-harassment-container .ant-input,
        .sexual-harassment-container .ant-picker {
          padding-left: 19px !important;
          padding-right: 19px !important;
        }
      `}} />
      <FormSection title="Sexual Harassment" columns={1}>
        <div className="flex flex-col gap-10 md:gap-[62px]">
          {/* Top Field */}
          <Form.Item
            label="Were you sexually harassed by a supervisor or coworker?"
            name={['sexual_harassment_info', 'harassed_by']}
            className="mb-0 w-full md:w-[30%]">
            <Select
              placeholder="Select"
              suffixIcon={<CurvDown className="pt-[10px] md:pt-[14px]" />}
              onChange={(value) => {
                if (value === 'Supervisor') {
                  form.setFieldValue(['sexual_harassment_info', 'notified_supervisor'], undefined);
                  form.setFieldValue(['sexual_harassment_info', 'action_taken'], undefined);
                  form.setFieldValue(['sexual_harassment_info', 'date_of_action'], undefined);
                  form.setFieldValue(['sexual_harassment_info', 'what_done_to_accused'], undefined);
                  form.setFieldValue(['sexual_harassment_info', 'harassment_stopped'], undefined);
                }
              }}
            >
              {harassedByOptions.map(option => (
                <Option key={option} value={option}>{option}</Option>
              ))}
            </Select>
          </Form.Item>

          {/* Dynamic Dates List */}
          <div className="mt-2">
            <Form.List name={['sexual_harassment_info', 'harassment_dates']} initialValue={[]}>
              {(fields, { add, remove }) => (
                <div className="flex flex-col gap-0 w-full">
                  <div className="flex items-center gap-10 mb-4">
                    <h3 className="font-['Lexend',sans-serif] text-[14px] lg:text-[18px] font-medium text-[#1F2A37] leading-[29px] m-0">
                      List up to 3 dates you were sexually harassed
                    </h3>
                    <Button
                      type="text"
                      onClick={() => add()}
                      disabled={fields.length >= 3 || isViewMode}
                      className="flex items-center gap-2 text-[#4682B4] font-['Lexend',sans-serif] p-0 disabled:opacity-50"
                    >
                      <span className="text-[18px] leading-none">+</span> Add More
                    </Button>
                  </div>

                  {fields.map(({ key, name, ...restField }) => (
                    <div key={key} className="flex flex-row items-start w-full md:w-[40%] mb-4">
                      {/* Number */}
                      <div className="w-8 flex justify-center pt-[45px] md:pt-[80px] mr-[8px] md:mr-[18px]">
                        <span className="font-['Lexend',sans-serif] text-[12px] md:text-[18px] text-[#1F2A37A1] font-medium">
                          {String(name + 1).padStart(2, '0')}
                        </span>
                      </div>

                      {/* Date Field */}
                      <div className="flex-1 max-w-[76%] mr-[10px] md:mr-[24px]">
                        <Form.Item
                          {...restField}
                          name={[name, 'date']}
                          label="Date"
                          className="mb-0"
                          rules={[{ required: true, message: 'Please enter the date' }]}
                        >
                          <DatePicker
                            className="w-full"
                            placeholder="Select Date"
                            format="DD-MM-YYYY"
                            inputReadOnly
                          />
                        </Form.Item>
                      </div>

                      {/* Delete */}
                      <div className="w-10 flex justify-center pt-[45px] md:pt-[80px]">
                        <button
                          onClick={() => {
                            if (!isView) {
                              remove(name);
                            }
                          }}
                          disabled={isView}
                          type="button"
                          style={isView ? { pointerEvents: 'none', opacity: 0.3, cursor: 'not-allowed' } : undefined}
                          className="p-1 text-[#6FB6F2] hover:text-red-500 transition-colors border-none bg-transparent cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none"
                        >
                          <svg width="20" height="22" viewBox="0 0 16 18" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-[12px] h-[13px] md:w-[18px] md:h-[20px] stroke-current">
                            <path d="M1 4h14M2 4v11a2 2 0 002 2h8a2 2 0 002-2V4M5 4V2a1 1 0 011-1h4a1 1 0 011 1v2M6.5 8v5M9.5 8v5" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Form.List>
          </div>

          {/* Bottom Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-x-[60px] gap-y-[16px] w-full">
            <Form.Item
              label="Did you notify a supervisor?"
              name={['sexual_harassment_info', 'notified_supervisor']}
              className={`mb-0 transition-opacity duration-300 ${(!harassedByWatch || disableIfHarassedBySupervisor) ? 'opacity-50' : 'opacity-100'}`}
            >
              <Select
                placeholder="Select"
                suffixIcon={<CurvDown className="pt-[10px] md:pt-[14px]" />}
                disabled={!harassedByWatch || disableIfHarassedBySupervisor}
                onChange={(val) => {
                  if (val === 'No') {
                    form.setFieldValue(['sexual_harassment_info', 'date_notified_supervisor'], undefined);
                  }
                }}
              >
                {yesNoOptions.map(option => (
                  <Option key={option} value={option}>{option}</Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="Date you notified a supervisor"
              name={['sexual_harassment_info', 'date_notified_supervisor']}
              className="mb-0"
            >
              <DatePicker className="w-full" placeholder="Select Date" format="DD-MM-YYYY" inputReadOnly />
            </Form.Item>

            <Form.Item
              label="Was the supervisor aware of the unwelcome attention?"
              name={['sexual_harassment_info', 'supervisor_aware_unwelcome_attention']}
              className="mb-0"
            >
              <Select placeholder="Select" suffixIcon={<CurvDown className="pt-[10px] md:pt-[14px]" />} >
                {yesNoOptions.map(option => (
                  <Option key={option} value={option}>{option}</Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="Was action taken?"
              name={['sexual_harassment_info', 'action_taken']}
              className={`mb-0 transition-opacity duration-300 ${(!harassedByWatch || disableIfHarassedBySupervisor) ? 'opacity-50' : 'opacity-100'}`}
            >
              <Select
                placeholder="Select"
                suffixIcon={<CurvDown className="pt-[10px] md:pt-[14px]" />}
                disabled={!harassedByWatch || disableIfHarassedBySupervisor}
                onChange={(val) => {
                  if (val === 'No') {
                    form.setFieldValue(['sexual_harassment_info', 'date_of_action'], undefined);
                    form.setFieldValue(['sexual_harassment_info', 'what_done_to_accused'], undefined);
                  }
                }}
              >
                {yesNoOptions.map(option => (
                  <Option key={option} value={option}>{option}</Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="Date of action"
              name={['sexual_harassment_info', 'date_of_action']}
              className={`mb-0 transition-opacity duration-300 ${disableActionTakenFields || disableIfHarassedBySupervisor || !harassedByWatch ? 'opacity-50' : 'opacity-100'}`}
            >
              <DatePicker className="w-full" placeholder="Select Date" format="DD-MM-YYYY" disabled={disableActionTakenFields || disableIfHarassedBySupervisor} inputReadOnly />
            </Form.Item>

            <Form.Item
              label="What was done to the accused?"
              name={['sexual_harassment_info', 'what_done_to_accused']}
              className={`mb-0 transition-opacity duration-300 ${disableActionTakenFields || disableIfHarassedBySupervisor || !harassedByWatch ? 'opacity-50' : 'opacity-100'}`}
            >
              <Select placeholder="Select" suffixIcon={<CurvDown className="pt-[10px] md:pt-[14px]" />} disabled={disableActionTakenFields || disableIfHarassedBySupervisor}>
                {whatDoneOptions.map(option => (
                  <Option key={option} value={option}>{option}</Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="Did the harassment stop?"
              name={['sexual_harassment_info', 'harassment_stopped']}
              className={`mb-0 transition-opacity duration-300 ${(!harassedByWatch || disableIfHarassedBySupervisor) ? 'opacity-50' : 'opacity-100'}`}
            >
              <Select placeholder="Select" suffixIcon={<CurvDown className="pt-[10px] md:pt-[14px]" />} disabled={!harassedByWatch || disableIfHarassedBySupervisor}>
                {yesNoOptions.map(option => (
                  <Option key={option} value={option}>{option}</Option>
                ))}
              </Select>
            </Form.Item>
          </div>
        </div>
      </FormSection>
    </div>
  );
};

export default SexualHarassment;